import { useEffect, useMemo, useState } from "react"
import {
  buildEffectStatViews,
  nextTickMs,
  nextTimerDelayMs,
  projectActiveEffects
} from "@/helpers/activeEffects"
import { serverNowMs } from "@/helpers/vitals"
import { useCatalogs } from "@/hooks/useCatalogs"
import { useHasSession } from "@/hooks/useHasSession"
import { useAppSelector } from "@/store"
import { useGetHeroMeQuery } from "@/store/apis/heroAPI"
import { PlayerSelectors } from "@/store/slices/player"
import { IActiveEffectView } from "@/types/activeEffects.types"

const EMPTY_VIEWS: IActiveEffectView[] = []
const COUNTDOWN_MIN_DELAY_MS = 1

export const useHeroActiveEffects = (): IActiveEffectView[] => {
  const hasSession = useHasSession()
  const isPlayerLoaded = useAppSelector(PlayerSelectors.selectIsPlayerLoaded())
  const { stats } = useCatalogs()

  const { activeEffects, serverTime, receivedAtMs } = useGetHeroMeQuery(
    undefined,
    {
      skip: !hasSession || !isPlayerLoaded,
      selectFromResult: ({ data, fulfilledTimeStamp }) => ({
        activeEffects: data?.activeEffects,
        serverTime: data?.serverTime,
        receivedAtMs: fulfilledTimeStamp
      })
    }
  )

  const statViews = useMemo(
    () => buildEffectStatViews(activeEffects, stats),
    [activeEffects, stats]
  )

  const [views, setViews] = useState<IActiveEffectView[]>(EMPTY_VIEWS)

  useEffect(() => {
    if (!serverTime || receivedAtMs === undefined) {
      setViews(EMPTY_VIEWS)
      return
    }

    let timerId: number | undefined

    const project = () => {
      const nowMs = serverNowMs(serverTime, receivedAtMs, Date.now())
      setViews(projectActiveEffects(statViews, nowMs))
      const tickAt = nextTickMs(statViews, nowMs)
      if (tickAt !== null) {
        timerId = window.setTimeout(
          project,
          nextTimerDelayMs(tickAt, nowMs, COUNTDOWN_MIN_DELAY_MS)
        )
      }
    }

    project()

    return () => window.clearTimeout(timerId)
  }, [statViews, serverTime, receivedAtMs])

  return views
}
